import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import './SalesScoreboard.css';
import './ComprehensiveSalesScoreboard.css';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import { getDocFee, docFeeIncome } from '../utils/docFee';
import { overUnderFor, avgOverUnder, tradeCount } from '../utils/overUnder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function abbreviateNumber(value) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
}

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + abbreviateNumber(Math.round(value));
}

function formatNumber(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '0';
  return abbreviateNumber(Number(value));
}

function calculatePercent(actual, target) {
  if (!target || target === 0) return 0;
  return Math.round((actual / target) * 100);
}

function ScoreboardDropdown({ options, value, onChange }) {
  return (
    <select className="scoreboard-dropdown" value={value} onChange={e => onChange(e.target.value)}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// Compute fiscal meta from dealership settings
function getFiscalMetaForDealership(dealership) {
  const mode = dealership?.performanceYearMode || 'CALENDAR';
  const startMonth = Math.min(Math.max(Number(dealership?.fiscalYearStartMonth) || 1, 1), 12);
  const startDay = Math.min(Math.max(Number(dealership?.fiscalYearStartDay) || 1, 1), 28);
  const today = new Date();
  let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);
  if (mode === 'FISCAL' && today < fiscalStart) {
    fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
  }
  const fiscalEnd = new Date(fiscalStart);
  fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
  const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
  const weeksInYear = Math.ceil(daysInYear / 7);
  const monthsInYear = 12;
  return { fiscalStart, fiscalEnd, daysInYear, daysElapsed, weeksInYear, monthsInYear };
}

// Get monthly target value from monthlyTargets array for current month
function getMonthlyTarget(targets, field, currentMonth) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }

  const monthData = targets.monthlyTargets.find(m => m.month === currentMonth + 1);
  if (!monthData) return 0;

  return Number(monthData[field]) || 0;
}

// MTD target = the SELECTED month's target prorated to the days elapsed through the
// reference date, so it lines up with the month-to-date actuals (which accumulate up
// to the same reference date) rather than summing the whole fiscal year. Uses the
// reference date's month/day so looking back at a past month shows that month's target
// instead of the current calendar month.
function getMTDTarget(targets, field, referenceDate = new Date()) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }
  const ref = new Date(referenceDate);
  const refMonth = ref.getMonth();
  const monthlyTarget = getMonthlyTarget(targets, field, refMonth);
  const daysInMonth = new Date(ref.getFullYear(), refMonth + 1, 0).getDate();
  const daysElapsed = Math.min(ref.getDate(), daysInMonth);
  return Math.round(monthlyTarget * (daysElapsed / daysInMonth));
}

// Convert stored target to requested period using fiscal-aware counts
function convertTargetValue(targets, field, period, fiscalMeta) {
  if (!targets) return 0;
  const base = Number(targets[field]) || 0;
  const activityFields = ['greets', 'sitDowns', 'writeUps', 'testRides'];
  const storedFreq = activityFields.includes(field)
    ? (targets.metricsFrequency || 'daily')
    : (targets.frontEndFrequency || 'yearly');

  const fromFreq = (storedFreq || 'yearly').toLowerCase();
  const toFreq = (period || 'yearly').toLowerCase();

  let yearlyTotal;
  switch (fromFreq) {
    case 'daily':
      yearlyTotal = base * fiscalMeta.daysInYear;
      break;
    case 'weekly':
      yearlyTotal = base * fiscalMeta.weeksInYear;
      break;
    case 'monthly':
      yearlyTotal = base * fiscalMeta.monthsInYear;
      break;
    case 'yearly':
    default:
      yearlyTotal = base;
  }

  switch (toFreq) {
    case 'daily':
      return Math.round(yearlyTotal / fiscalMeta.daysInYear);
    case 'weekly':
      return Math.round(yearlyTotal / fiscalMeta.weeksInYear);
    case 'monthly':
      return Math.round(yearlyTotal / fiscalMeta.monthsInYear);
    case 'yearly':
    default:
      return Math.round(yearlyTotal);
  }
}

function computeMetrics(logs, activities, targets, dealership, referenceDate = new Date()) {
  const refDate = new Date(referenceDate);
  const now = refDate;
  const month = now.getMonth();
  const year = now.getFullYear();

  const fiscalMeta = getFiscalMetaForDealership(dealership);

  const getUTCDateComponents = (dateStr) => {
    const d = new Date(dateStr);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      date: d.getUTCDate()
    };
  };

  const refDateComp = {
    year: refDate.getFullYear(),
    month: refDate.getMonth(),
    date: refDate.getDate()
  };

  const monthlyLogs = logs.filter((e) => {
    const logDateComp = getUTCDateComponents(e.date);
    return logDateComp.month === month &&
           logDateComp.year === year &&
           (logDateComp.year < year ||
            (logDateComp.year === year && logDateComp.month < month) ||
            (logDateComp.year === year && logDateComp.month === month && logDateComp.date <= refDateComp.date));
  });

  const sumLogs = (list, field) => list.reduce((sum, e) => sum + (Number(e[field]) || 0), 0);

  // The MTD activity cards compare month-to-date actuals against the target, so
  // prorate the full-month target to days elapsed (consistent with getMTDTarget).
  const daysInRefMonth = new Date(year, month + 1, 0).getDate();
  const mtdFactor = Math.min(refDateComp.date, daysInRefMonth) / daysInRefMonth;
  const mtdActivityTarget = (field) =>
    Math.round(convertTargetValue(targets, field, 'monthly', fiscalMeta) * mtdFactor);

  const greets = {
    monthly: sumLogs(monthlyLogs, 'greets'),
    monthlyTarget: mtdActivityTarget('greets')
  };

  const sitDowns = {
    monthly: sumLogs(monthlyLogs, 'sitDowns'),
    monthlyTarget: mtdActivityTarget('sitDowns')
  };

  const writeUps = {
    monthly: sumLogs(monthlyLogs, 'writeUps'),
    monthlyTarget: mtdActivityTarget('writeUps')
  };

  const testRides = {
    monthly: sumLogs(monthlyLogs, 'testRides'),
    monthlyTarget: mtdActivityTarget('testRides')
  };

  const monthlyActivities = activities.filter((a) => {
    const saleDateComp = getUTCDateComponents(a.saleDate);
    return saleDateComp.month === month &&
           saleDateComp.year === year &&
           (saleDateComp.year < year ||
            (saleDateComp.year === year && saleDateComp.month < month) ||
            (saleDateComp.year === year && saleDateComp.month === month && saleDateComp.date <= refDateComp.date));
  });

  // Over/under on a trade is a cost of moving the unit, so it comes out of that
  // unit's gross at report time. The stored totalCost is left alone.
  const buildSalesBlock = (list, filterNew) => {
    const subset = list.filter((a) => {
      const val = (a.motorcycleNewUsed || '').toUpperCase();
      return filterNew ? val === 'NEW' : val === 'USED';
    });
    const count = subset.length;
    const gross = subset.reduce((sum, a) => {
      const price = Number(a.salesPrice) || 0;
      const cost = Number(a.totalCost) || 0;
      return sum + (price - cost - (overUnderFor(a) || 0));
    }, 0);
    return { count, gross };
  };

  const monthlyNew = buildSalesBlock(monthlyActivities, true);
  const monthlyUsed = buildSalesBlock(monthlyActivities, false);

  const newUnitsMTD = getMTDTarget(targets, 'newUnits', refDate);
  const usedUnitsMTD = getMTDTarget(targets, 'usedUnits', refDate);
  const newGrossMTD = getMTDTarget(targets, 'newGrossProfit', refDate);
  const usedGrossMTD = getMTDTarget(targets, 'usedGrossProfit', refDate);
  // Back End GP target maps to F&I Reserve; F&I GP target maps to F&I Product
  // (same mapping as the Sales Performance dashboard).
  const backEndGrossMTD = getMTDTarget(targets, 'backEndGrossProfit', refDate);
  const fiGrossMTD = getMTDTarget(targets, 'fiGrossProfit', refDate);
  const fiCombinedMTD = backEndGrossMTD + fiGrossMTD;

  const totalUnitsMTD = newUnitsMTD + usedUnitsMTD;
  const totalGrossMTD = newGrossMTD + usedGrossMTD + backEndGrossMTD;

  const monthlyTotalUnits = monthlyNew.count + monthlyUsed.count;
  const monthlyTotalGross = monthlyNew.gross + monthlyUsed.gross;

  // Doc fee is back-end gross too, so it joins the total while the reserve and
  // product breakdowns stay as F&I actually wrote them.
  const docFee = getDocFee(targets);
  const calculateFI = (activityList) => {
    const fiReserve = activityList.reduce((sum, a) => sum + (Number(a.fiReserve) || 0), 0);
    const fiProduct = activityList.reduce((sum, a) => sum + (Number(a.fiProduct) || 0), 0);
    const fiDocFee = docFeeIncome(activityList, docFee);
    return { fiReserve, fiProduct, fiDocFee, fiTotal: fiReserve + fiProduct + fiDocFee };
  };

  const monthlyFI = calculateFI(monthlyActivities);

  return {
    greets,
    sitDowns,
    writeUps,
    testRides,
    newUnits: {
      monthly: monthlyNew.count,
      monthlyTarget: newUnitsMTD
    },
    newGross: {
      monthly: monthlyNew.gross,
      monthlyTarget: newGrossMTD
    },
    usedUnits: {
      monthly: monthlyUsed.count,
      monthlyTarget: usedUnitsMTD
    },
    usedGross: {
      monthly: monthlyUsed.gross,
      monthlyTarget: usedGrossMTD
    },
    frontendTotal: {
      monthly: monthlyTotalGross,
      monthlyTarget: totalGrossMTD
    },
    frontendPerUnit: {
      monthly: monthlyTotalUnits > 0 ? monthlyTotalGross / monthlyTotalUnits : 0,
      monthlyTarget: totalUnitsMTD > 0 ? totalGrossMTD / totalUnitsMTD : 0
    },
    fiReserve: {
      monthly: monthlyFI.fiReserve,
      monthlyTarget: backEndGrossMTD
    },
    fiProduct: {
      monthly: monthlyFI.fiProduct,
      monthlyTarget: fiGrossMTD
    },
    fiTotal: {
      monthly: monthlyFI.fiTotal,
      monthlyTarget: fiCombinedMTD
    },
    fiPerUnit: {
      monthly: monthlyTotalUnits > 0 ? monthlyFI.fiTotal / monthlyTotalUnits : 0,
      monthlyTarget: totalUnitsMTD > 0 ? fiCombinedMTD / totalUnitsMTD : 0
    },
    totalPerUnit: {
      monthly: monthlyTotalUnits > 0 ? (monthlyTotalGross + monthlyFI.fiTotal) / monthlyTotalUnits : 0,
      monthlyTarget: totalUnitsMTD > 0 ? (totalGrossMTD + fiGrossMTD) / totalUnitsMTD : 0
    },
    // No target: an over-allowance isn't a goal, it's a leak to keep small.
    // Null means no deals carried a trade this month.
    avgOverUnder: {
      monthly: avgOverUnder(monthlyActivities),
      deals: tradeCount(monthlyActivities)
    }
  };
}

export default function ComprehensiveSalesScoreboard({ user, managedOwner } = {}) {
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState('');
  const [selectedDealershipObj, setSelectedDealershipObj] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [metrics, setMetrics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hasTargets, setHasTargets] = useState(true);

  useEffect(() => {
    async function fetchDealerships() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // In owner / managed-owner view, scope to that owner's dealerships so a
        // site manager viewing a specific owner doesn't see (or pick) every
        // dealership on the platform. Otherwise fall back to the caller's own
        // accessible dealerships.
        const isOwnerView = user?.role === 'OWNER' || managedOwner;
        let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
        if (managedOwner && isOwnerView) {
          url += `?ownerId=${managedOwner.id}`;
        }
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error('Failed to load dealerships');
        const json = await res.json();
        setDealerships(json.dealerships || []);
        if (json.dealerships && json.dealerships.length > 0) {
          setSelectedDealership(json.dealerships[0].id);
          setSelectedDealershipObj(json.dealerships[0]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load dealerships');
      } finally {
        setLoading(false);
      }
    }
    fetchDealerships();
  }, [user, managedOwner]);

  useEffect(() => {
    if (!selectedDealership) return;
    async function fetchMetrics() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Calculate MTD date range based on selected date
        const refDate = new Date(selectedDate);
        const year = refDate.getFullYear();
        const month = refDate.getMonth();
        const startOfMonth = new Date(year, month, 1);
        const endOfMonth = new Date(year, month, refDate.getDate(), 23, 59, 59, 999); // End of selected date

        // Format dates as YYYY-MM-DD
        const formatDate = (date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        };

        const startDate = formatDate(startOfMonth);
        const endDate = formatDate(endOfMonth);

        const [logsRes, salesRes, targetsRes, leaderboardRes] = await Promise.all([
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/daily-logs`, { headers }),
          fetch(`${API_URL}/api/dealerships/${selectedDealership}/sales-activities`, { headers }),
          fetch(`${API_URL}/api/targets/${selectedDealership}`, { headers }),
          fetch(`${API_URL}/api/analytics/leaderboard/sales?dealershipIds=${selectedDealership}&period=monthly&startDate=${startDate}&endDate=${endDate}`, { headers })
        ]);

        const logsData = logsRes.ok ? await logsRes.json() : { entries: [] };
        const salesData = salesRes.ok ? await salesRes.json() : { activities: [] };
        const targetsData = targetsRes.ok ? await targetsRes.json() : { frontEnd: {} };

        if (!leaderboardRes.ok) {
          console.warn('Leaderboard request failed:', leaderboardRes.status, await leaderboardRes.text());
        }
        const leaderboardData = leaderboardRes.ok ? await leaderboardRes.json() : { leaderboard: [] };

        // When the dealership disables "always show F&I", unclosed deals don't
        // count toward any report figures until they're marked closed/committed.
        const visibleActivities = (selectedDealershipObj?.showUnclosedFI !== false)
          ? (salesData.activities || [])
          : (salesData.activities || []).filter((a) => a.closed);

        const computedMetrics = computeMetrics(
          logsData.entries || [],
          visibleActivities,
          targetsData.frontEnd || {},
          selectedDealershipObj,
          selectedDate
        );

        setMetrics(computedMetrics);
        setHasTargets(Array.isArray(targetsData.frontEnd?.monthlyTargets) && targetsData.frontEnd.monthlyTargets.length > 0);
        setLeaderboard(leaderboardData.leaderboard || []);
      } catch (err) {
        setError(err.message || 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, [selectedDealership, selectedDealershipObj, selectedDate]);

  const handleDealershipChange = (dealershipId) => {
    setSelectedDealership(dealershipId);
    const dealershipObj = dealerships.find(d => d.id === dealershipId);
    setSelectedDealershipObj(dealershipObj || null);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement || !!document.mozFullScreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullscreen = () => {
    const scoreboardElement = document.querySelector('.comprehensive-sales-scoreboard');
    if (!fullscreen && scoreboardElement) {
      if (scoreboardElement.requestFullscreen) {
        scoreboardElement.requestFullscreen();
      } else if (scoreboardElement.webkitRequestFullscreen) {
        scoreboardElement.webkitRequestFullscreen();
      } else if (scoreboardElement.mozRequestFullScreen) {
        scoreboardElement.mozRequestFullScreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
    }
  };

  const percentClass = (pct) => {
    if (pct >= 80) return 'high';
    if (pct >= 50) return 'mid';
    return 'low';
  };

  const getTrendIndicator = (pct) => {
    if (pct >= 80) return { label: 'On Track', class: 'trend-on-track' };
    if (pct >= 50) return { label: 'Caution', class: 'trend-caution' };
    return { label: 'Behind', class: 'trend-behind' };
  };

  if (loading) return <div className="scoreboard-loading">Loading...</div>;
  if (error) return <div className="scoreboard-error">{error}</div>;
  if (!dealerships.length) return <div className="scoreboard-error">No dealerships found.</div>;
  if (!metrics) return <div className="scoreboard-loading">Loading metrics...</div>;
  if (!hasTargets) return <NoTargetsPlaceholder />;

  const m = metrics;

  // Helper to render a dashboard card (top row - simplified version)
  const renderDashboardCard = (label, value, target, formatter = (v) => v) => {
    const pct = calculatePercent(value, target);
    const trend = getTrendIndicator(pct);

    return (
      <div className="dashboard-summary-card">
        <div className="dashboard-card-label">{label}</div>
        <div className="dashboard-card-value">{formatter(value)}</div>
        <div className="dashboard-card-target">Target: {formatter(target)}</div>
        <div className={`dashboard-card-percent ${percentClass(pct)}`}>{pct}%</div>
        <div className={`trend-indicator ${trend.class}`}>{trend.label}</div>
      </div>
    );
  };

  // Helper to render detailed metric cards (bottom section)
  const renderDetailedCard = (label, metric, formatter = (v) => v) => {
    const pct = calculatePercent(metric.monthly, metric.monthlyTarget);
    const trend = getTrendIndicator(pct);

    return (
      <div className="kpi-card">
        <div className="kpi-label">{label}</div>
        <div className="kpi-compare-multi">
          <div className="kpi-period-block">
            <div className="kpi-sub">MTD</div>
            <div className="kpi-value">{formatter(metric.monthly)}</div>
            <div className="kpi-target">Target: {formatter(metric.monthlyTarget)}</div>
            <div className={`kpi-percent ${percentClass(pct)}`}>{pct}%</div>
            <div className="kpi-meter-bar">
              <div className={`kpi-meter-fill ${percentClass(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }}></div>
            </div>
            <div className={`trend-indicator ${trend.class}`}>{trend.label}</div>
          </div>
        </div>
      </div>
    );
  };

  // Over/under has no target, so it skips the meter and percent that
  // renderDetailedCard assumes. Positive = money given away on trades.
  const renderOverUnderCard = (metric) => {
    const value = metric.monthly;
    const deals = metric.deals || 0;
    const tone = value === null || value === 0 ? 'mid' : value > 0 ? 'low' : 'high';
    const label = value === null
      ? '—'
      : `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`;
    return (
      <div className="kpi-card">
        <div className="kpi-label">Avg Over/Under per Bike</div>
        <div className="kpi-compare-multi">
          <div className="kpi-period-block">
            <div className="kpi-sub">MTD</div>
            <div className="kpi-value">{label}</div>
            <div className={`kpi-percent ${tone}`}>
              {value === null ? 'No trades' : value > 0 ? 'Over' : value < 0 ? 'Under' : 'At book'}
            </div>
            <div className="kpi-target">
              {deals === 0 ? '—' : `${deals} deal${deals === 1 ? '' : 's'} with a trade`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="comprehensive-sales-scoreboard">
      <div className="scoreboard-header">
        <h1><span style={{ color: '#ff8c00', fontSize: '1.2em' }}>{dealerships.find(d => d.id === selectedDealership)?.name || ''}: Sales Leader Scoreboard</span></h1>
        <div className="scoreboard-controls">
          <ScoreboardDropdown
            options={dealerships.map(d => ({ value: d.id, label: d.name }))}
            value={selectedDealership}
            onChange={handleDealershipChange}
          />
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
          <button className="fullscreen-btn" onClick={handleFullscreen}>
            {fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
        </div>
      </div>

      <div className="comprehensive-content">
        {/* Top Row: Dashboard Cards */}
        <div className="dashboard-summary-row">
          {renderDashboardCard('New Units', m.newUnits.monthly, m.newUnits.monthlyTarget, formatNumber)}
          {renderDashboardCard('Used Units', m.usedUnits.monthly, m.usedUnits.monthlyTarget, formatNumber)}
          {renderDashboardCard('Greets', m.greets.monthly, m.greets.monthlyTarget, formatNumber)}
          {renderDashboardCard('Sit Downs', m.sitDowns.monthly, m.sitDowns.monthlyTarget, formatNumber)}
          {renderDashboardCard('Test Rides', m.testRides.monthly, m.testRides.monthlyTarget, formatNumber)}
          {renderDashboardCard('Write Ups', m.writeUps.monthly, m.writeUps.monthlyTarget, formatNumber)}
        </div>

        {/* Sales Leaderboard */}
        <div className="leaderboard-section">
          <h3 className="leaderboard-title">Sales Leaderboard (MTD)</h3>
          <div className="leaderboard-table-wrapper">
            {leaderboard.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', textAlign: 'center', margin: '20px 0' }}>
                No data available
              </p>
            ) : (
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Dealership</th>
                    <th>New Bikes</th>
                    <th>Used Bikes</th>
                    <th>Greets</th>
                    <th>Sit Downs</th>
                    <th>Test Rides</th>
                    <th>Write Ups</th>

                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((performer, idx) => (
                    <tr key={performer.employeeId || idx} className={idx < 3 ? `top-${idx + 1}` : ''}>
                      <td className="rank-cell">#{idx + 1}</td>
                      <td className="name-cell">{performer.name || 'Unknown'}</td>
                      <td>{performer.dealershipName || ''}</td>
                      <td>{performer.newBikesSold || 0}</td>
                      <td>{performer.usedBikesSold || 0}</td>
                      <td>{performer.greets || 0}</td>
                      <td>{performer.sitDowns || 0}</td>
                      <td>{performer.testRides || 0}</td>
                      <td>{performer.writeUps || 0}</td>

                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Detailed Metrics Cards */}
        <div className="detailed-metrics-section">
          <div className="kpi-grid">
            {renderDetailedCard('New Unit Gross Profit', m.newGross, formatCurrency)}
            {renderDetailedCard('Used Unit Gross Profit', m.usedGross, formatCurrency)}
            {renderDetailedCard('Frontend Total', m.frontendTotal, formatCurrency)}
            {renderDetailedCard('Frontend per Unit', m.frontendPerUnit, formatCurrency)}
            {renderDetailedCard('F&I Reserves Gross Profit', m.fiReserve, formatCurrency)}
            {renderDetailedCard('F&I Product Gross Profit', m.fiProduct, formatCurrency)}
            {renderDetailedCard('F&I Total', m.fiTotal, formatCurrency)}
            {renderOverUnderCard(m.avgOverUnder)}
            {renderDetailedCard('F&I per Unit', m.fiPerUnit, formatCurrency)}
            {renderDetailedCard('Total per Unit', m.totalPerUnit, formatCurrency)}
          </div>
        </div>
      </div>
    </div>
  );
}
